// Markdown links and bare https:// addresses (as the team pastes them) become links. Only
// HTTPS, and punctuation ending a sentence ("…/malaga.") stays text.
const LINK = /\[([^\]]+)\]\((https:\/\/[^\s)]+)\)|https:\/\/[^\s<>()[\]]+/g;
function messageParts(value) {
  const text = String(value || "").replace(/\*\*/g, "");
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const [whole, label, target] = match;
    const url = target || whole.replace(/[.,;:!?'"’”…]+$/, "");
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: label || url, url });
    last = match.index + (target ? whole.length : url.length);
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
function isSendShortcut(event) {
  return (
    event.key === "Enter" &&
    (event.metaKey || event.ctrlKey) &&
    !event.isComposing
  );
}
// The words this file puts in the chat. Plain Node tests load it, so it can't use the
// app's languages: SupportWidget passes them translated (and the date locale).
const LABELS = {
  attachment: "Attachment",
  fin: "Fin · previous AI assistant",
  staff: "EventLive support team",
  staffNamed: (name) => `${name} · EventLive support`,
  assistant: "EventLive AI assistant",
  dateLocale: undefined,
};
// Photos and files from the server: images show inline, anything else is a download link.
// Only HTTPS links are kept (plain http only for a local development server).
function safeAttachments(list, labels = LABELS) {
  return (Array.isArray(list) ? list : [])
    .filter((a) => {
      try {
        const url = new URL(a.url);
        return (
          typeof a.id === "string" &&
          (url.protocol === "https:" ||
            (url.protocol === "http:" &&
              ["localhost", "127.0.0.1"].includes(url.hostname)))
        );
      } catch {
        return false;
      }
    })
    .map((a) => ({
      id: a.id,
      kind: a.kind === "image" ? "image" : "file",
      name: typeof a.name === "string" && a.name ? a.name : labels.attachment,
      url: a.url,
      width: Number(a.width) || null,
      height: Number(a.height) || null,
    }));
}
function mergeSupportHistory(messages, pending, current = [], labels = LABELS) {
  const items = messages.map((m, i) => {
    const id = m.request_id
      ? m.role === "user"
        ? m.request_id
        : `${m.role}:${m.request_id}`
      : `saved-${i}`;
    const attachments = safeAttachments(m.attachments, labels);
    return {
      id,
      role: m.role,
      text: m.body,
      sources: m.sources,
      attachments,
      // The photo this device just sent, until the server has it.
      photo: attachments.length
        ? undefined
        : current.find((item) => item.id === id)?.photo,
    };
  });
  if (
    pending &&
    !messages.some(
      (m) => m.role === "user" && m.request_id === pending.requestId
    )
  ) {
    const optimistic = current.find((item) => item.id === pending.requestId);
    if (optimistic) items.push(optimistic);
  }
  return items;
}
function additionalSources(text, sources = []) {
  const key = (value) => {
    try {
      const url = new URL(value);
      if (url.protocol !== "https:") return null;
      // Help-center titles/slugs can differ while referring to the same article.
      const article = url.pathname.match(/^(?:\/[^/]+)?\/articles\/(\d+)/);
      return article
        ? `${url.host}/articles/${article[1]}`
        : `${url.host}${url.pathname.replace(/\/$/, "")}${url.search}`;
    } catch {
      return null;
    }
  };
  const seen = new Set(
    messageParts(text)
      .filter((part) => part.url)
      .map((part) => key(part.url))
  );
  return (sources || []).filter((source) => {
    const id = key(source.url);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}
function speakerLabel(message, labels = LABELS) {
  if (message.role === "fin") return labels.fin;
  if (message.role === "staff")
    return message.author
      ? labels.staffNamed(String(message.author).split(" ")[0])
      : labels.staff;
  if (message.role === "assistant") return labels.assistant;
  return null;
}
function formatDay(value, locale) {
  const text = String(value || "");
  const date = new Date(
    text.includes("T") ? text : `${text.replace(" ", "T")}Z`
  );
  if (Number.isNaN(date.getTime())) return "";
  try {
    return date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return date.toISOString().slice(0, 10);
  }
}
// Flatten earlier conversations (oldest first) into read-only chat items with a
// dated divider per conversation; Fin and staff replies carry explicit labels.
function earlierItems(conversations = [], labels = LABELS) {
  const items = [];
  for (const c of conversations || []) {
    if (!c || !Array.isArray(c.messages) || !c.messages.length) continue;
    items.push({
      id: `earlier-${c.id}`,
      role: "divider",
      text: [formatDay(c.updatedAt || c.createdAt, labels.dateLocale), c.title]
        .filter(Boolean)
        .join(" · "),
    });
    c.messages.forEach((m, i) =>
      items.push({
        id: `earlier-${c.id}-${i}`,
        role: m.role,
        text: m.body,
        label: speakerLabel(m, labels),
        attachments: safeAttachments(m.attachments, labels),
        earlier: true,
      })
    );
  }
  return items;
}
// Team replies arrive only by polling; counting them drives the unread dot.
function teamReplyCount(messages = []) {
  return (messages || []).filter((m) => m.role === "staff").length;
}
// The website that embeds the chat, only if it's on the allowed list.
function allowedParent(value, allowed = []) {
  try {
    const origin = new URL(value).origin;
    return allowed.includes(origin) ? origin : null;
  } catch {
    return null;
  }
}
// The page a website visitor is on, as Luna sees it: "evt.live/smith-wedding/download",
// "eventlive.pro/pricing". Without a known parent, assume eventlive.pro.
function websiteSurface(parent, path) {
  let site = "eventlive.pro";
  try {
    site = new URL(parent).host.replace(/^www\./, "");
  } catch {}
  return `${site}${path || ""}`;
}
// eventlive.pro's YouthStream pages: /youthstream-app and YouthStream's help (the category
// /help/category/youthstream and /help/youthstream-… articles). A visitor who chats there gets
// YouthStream's answers (evt.live lib/support/youthstream.cjs isYouthStreamPage reads the same
// paths), so the chat talks about YouthStream too.
function isYouthStreamPath(path) {
  return (
    typeof path === "string" &&
    /^\/(?:youthstream-app|help\/category\/youthstream|help\/youthstream-[a-z0-9-]+)(?:[/?#]|$)/i.test(path)
  );
}
module.exports = {
  teamReplyCount,
  allowedParent,
  websiteSurface,
  isYouthStreamPath,
  safeAttachments,
  messageParts,
  isSendShortcut,
  mergeSupportHistory,
  additionalSources,
  earlierItems,
};
